// ─────────────────────────────────────────────────────────────
// PropFirmComplianceWidget
//
// Prop firm compliance dashboard for funded futures traders.
// Shows four live gauges calculated from the account's trade data:
//
//   1. Drawdown Buffer  — how much room remains before violating
//                         the max drawdown limit (EOD or intraday
//                         trailing, depending on drawdown_type)
//   2. Daily Loss Used  — today's loss vs the daily loss limit
//   3. Profit Target    — cumulative P&L toward the eval target
//   4. Consistency Rule — best-day % of total profit
//
// Props:
//   account       — account row (needs drawdown_limit,
//                   drawdown_type, profit_target, consistency_rule,
//                   starting_balance, prop_firm, account_type)
//   trades        — ALL trades for this account (full history)
//   onSetup       — callback to open Account compliance setup
//
// IMPORTANT: "Real-time" here means computed from synced trade
// data. This is not a live tick feed from the broker. We show
// session-end/realized P&L which is what prop firms typically
// enforce on EOD trailing accounts. Always verify against your
// firm's live dashboard before making risk decisions.
// ─────────────────────────────────────────────────────────────

import { useMemo, useState, useCallback } from "react";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { fmt$, getActiveSessionDate } from "../../lib/format";

// ─────────────────────────────────────────────────────────────
// Firm presets — pre-fills drawdown_limit / profit_target when
// a new account selects a known firm. The user always overrides.
// ─────────────────────────────────────────────────────────────
export const FIRM_COMPLIANCE_PRESETS = {
  "Topstep": {
    "50000":  { drawdown_limit: 2000, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0.50 },
    "100000": { drawdown_limit: 3000, profit_target: 6000, drawdown_type: "eod_trailing",    consistency_rule: 0.50 },
    "150000": { drawdown_limit: 4500, profit_target: 9000, drawdown_type: "eod_trailing",    consistency_rule: 0.50 },
    default:  { drawdown_limit: 2000, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0.50 },
  },
  "Apex Trader Funding": {
    "25000":  { drawdown_limit: 1500, profit_target: 1500,  drawdown_type: "eod_trailing",   consistency_rule: 0.50 },
    "50000":  { drawdown_limit: 2500, profit_target: 3000,  drawdown_type: "eod_trailing",   consistency_rule: 0.50 },
    "100000": { drawdown_limit: 3000, profit_target: 6000,  drawdown_type: "eod_trailing",   consistency_rule: 0.50 },
    "150000": { drawdown_limit: 5000, profit_target: 9000,  drawdown_type: "eod_trailing",   consistency_rule: 0.50 },
    "250000": { drawdown_limit: 6500, profit_target: 15000, drawdown_type: "eod_trailing",   consistency_rule: 0.50 },
    "300000": { drawdown_limit: 7500, profit_target: 20000, drawdown_type: "eod_trailing",   consistency_rule: 0.50 },
    default:  { drawdown_limit: 2500, profit_target: 3000,  drawdown_type: "eod_trailing",   consistency_rule: 0.50 },
  },
  "MyFundedFutures": {
    "50000":  { drawdown_limit: 2000, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0 },
    "100000": { drawdown_limit: 3500, profit_target: 6000, drawdown_type: "eod_trailing",    consistency_rule: 0 },
    "150000": { drawdown_limit: 5000, profit_target: 8000, drawdown_type: "eod_trailing",    consistency_rule: 0 },
    default:  { drawdown_limit: 2000, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0 },
  },
  "Tradeify": {
    "50000":  { drawdown_limit: 2000, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0.30 },
    "100000": { drawdown_limit: 3000, profit_target: 6000, drawdown_type: "eod_trailing",    consistency_rule: 0.30 },
    default:  { drawdown_limit: 2000, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0.30 },
  },
  "Take Profit Trader": {
    "50000":  { drawdown_limit: 2500, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0 },
    default:  { drawdown_limit: 2500, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0 },
  },
  "Earn2Trade": {
    "25000":  { drawdown_limit: 1500, profit_target: 1500, drawdown_type: "eod_trailing",    consistency_rule: 0.30 },
    "50000":  { drawdown_limit: 2000, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0.30 },
    default:  { drawdown_limit: 2000, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0.30 },
  },
  "Bulenox": {
    "50000":  { drawdown_limit: 2500, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0 },
    default:  { drawdown_limit: 2500, profit_target: 3000, drawdown_type: "eod_trailing",    consistency_rule: 0 },
  },
};

// Helper: get a preset for a given firm + balance
export function getFirmPreset(propFirm, startingBalance) {
  const firmPresets = FIRM_COMPLIANCE_PRESETS[propFirm];
  if (!firmPresets) return null;
  const balKey = String(Math.round(startingBalance || 0));
  return firmPresets[balKey] || firmPresets.default || null;
}

// ─────────────────────────────────────────────────────────────
// Math helpers
// ─────────────────────────────────────────────────────────────
function tradeNet(t) {
  return (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
}

function todayDateET() {
  try { return getActiveSessionDate(); } catch {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  }
}

// EOD trailing drawdown floor calculation.
// The floor trails the CLOSING balance of each session day.
// It never goes down. We replay all trade days to find the peak
// closing balance, then floor = peakClose - drawdown_limit.
// Buffer = currentBalance - floor.
function calcEodTrailingBuffer(trades, startingBalance, drawdownLimit) {
  if (!drawdownLimit || drawdownLimit <= 0) return null;

  // Group trades by date
  const byDate = {};
  trades.forEach(t => {
    const d = String(t.trade_date || "").slice(0, 10);
    if (!d) return;
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(t);
  });

  const sortedDays = Object.keys(byDate).sort();

  let runningBalance = startingBalance;
  let peakClosingBalance = startingBalance;

  sortedDays.forEach(day => {
    const dayPnL = byDate[day].reduce((s, t) => s + tradeNet(t), 0);
    runningBalance += dayPnL;
    // EOD: floor updates based on closing balance
    if (runningBalance > peakClosingBalance) {
      peakClosingBalance = runningBalance;
    }
  });

  const floor = peakClosingBalance - drawdownLimit;
  const buffer = runningBalance - floor;

  return {
    currentBalance: runningBalance,
    peakBalance: peakClosingBalance,
    floor,
    buffer: Math.max(0, buffer),
    bufferPct: drawdownLimit > 0 ? Math.min(100, Math.max(0, (buffer / drawdownLimit) * 100)) : 0,
  };
}

// Static drawdown: floor is always startingBalance - drawdownLimit
function calcStaticBuffer(trades, startingBalance, drawdownLimit) {
  if (!drawdownLimit || drawdownLimit <= 0) return null;
  const totalPnL = trades.reduce((s, t) => s + tradeNet(t), 0);
  const currentBalance = startingBalance + totalPnL;
  const floor = startingBalance - drawdownLimit;
  const buffer = currentBalance - floor;
  return {
    currentBalance,
    peakBalance: startingBalance,
    floor,
    buffer: Math.max(0, buffer),
    bufferPct: drawdownLimit > 0 ? Math.min(100, Math.max(0, (buffer / drawdownLimit) * 100)) : 0,
  };
}

// Today's realized P&L
function calcTodayPnL(trades) {
  const today = todayDateET();
  return trades
    .filter(t => String(t.trade_date || "").slice(0, 10) === today)
    .reduce((s, t) => s + tradeNet(t), 0);
}

// Profit target progress — sum of all realized P&L since account start
function calcProfitProgress(trades, startingBalance) {
  const totalPnL = trades.reduce((s, t) => s + tradeNet(t), 0);
  return totalPnL;
}

// Consistency rule: best single day / total profit
// Returns { bestDay, totalProfit, consistencyPct, limit }
function calcConsistencyScore(trades, consistencyRule) {
  const byDate = {};
  trades.forEach(t => {
    const d = String(t.trade_date || "").slice(0, 10);
    if (!d) return;
    byDate[d] = (byDate[d] || 0) + tradeNet(t);
  });

  const days = Object.values(byDate);
  const profitDays = days.filter(v => v > 0);
  if (profitDays.length === 0) return null;

  const bestDay = Math.max(...profitDays);
  const totalProfit = profitDays.reduce((s, v) => s + v, 0);
  const consistencyPct = totalProfit > 0 ? bestDay / totalProfit : 0;

  return {
    bestDay,
    totalProfit,
    consistencyPct,
    limit: consistencyRule || 0,
    isViolation: consistencyRule > 0 && consistencyPct > consistencyRule,
  };
}

// ─────────────────────────────────────────────────────────────
// Main widget
// ─────────────────────────────────────────────────────────────
export function PropFirmComplianceWidget({ account: accountProp, trades = [], onSetup, onSaved }) {
  // Local account state so compliance fields update instantly after save
  const [account, setAccount] = useState(accountProp);
  // Sync if parent passes a new account object (e.g. account switch)
  useMemo(() => { setAccount(accountProp); }, [accountProp?.id]);

  const {
    prop_firm,
    account_type,
    starting_balance = 0,
    drawdown_limit,
    drawdown_type = "eod_trailing",
    profit_target,
    consistency_rule,
    daily_loss_limit,
  } = account || {};

  const isConfigured = !!(drawdown_limit);

  // Inline setup modal state — no page navigation needed
  const [showSetup, setShowSetup] = useState(false);
  const handleSetup = useCallback(() => {
    if (onSetup) { onSetup(); } else { setShowSetup(true); }
  }, [onSetup]);

  // All compliance math runs only when configured
  const compliance = useMemo(() => {
    if (!isConfigured || !account) return null;

    const ddResult = drawdown_type === "static"
      ? calcStaticBuffer(trades, starting_balance, drawdown_limit)
      : calcEodTrailingBuffer(trades, starting_balance, drawdown_limit);

    const todayPnL = calcTodayPnL(trades);
    const totalPnL = calcProfitProgress(trades, starting_balance);
    const consistency = consistency_rule > 0
      ? calcConsistencyScore(trades, consistency_rule)
      : null;

    // Daily loss: how much of the daily limit has been used today
    const dllUsed = Math.max(0, -todayPnL);
    const dllLimit = daily_loss_limit || null;
    const dllPct   = dllLimit > 0 ? Math.min(100, (dllUsed / dllLimit) * 100) : null;

    // Profit target progress
    const targetPct = profit_target > 0
      ? Math.min(100, Math.max(0, (totalPnL / profit_target) * 100))
      : null;

    return { ddResult, todayPnL, totalPnL, consistency, dllUsed, dllLimit, dllPct, targetPct };
  }, [account, trades, isConfigured, drawdown_limit, drawdown_type, starting_balance,
      consistency_rule, daily_loss_limit, profit_target]);

  // ── Not configured ──────────────────────────────────────────
  if (!account) return null;

  if (!isConfigured) {
    return (
      <Card>
        <SectionTitle>📊 Prop Firm Compliance</SectionTitle>
        <div style={{
          padding: "16px", background: C.blue + "10",
          border: `1px solid ${C.blue}30`, borderRadius: 8,
          fontSize: 13, color: C.sub, lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>
            Set up compliance tracking for this account
          </div>
          <div style={{ marginBottom: 12 }}>
            Add your drawdown limit, daily loss limit, and profit target to track
            how close you are to violating your prop firm's rules.
          </div>
          {true && (
            <button
              onClick={handleSetup}
              style={{
                padding: "8px 16px", background: C.blue, color: "#fff",
                border: "none", borderRadius: 6, fontWeight: 700,
                fontSize: 12, cursor: "pointer",
              }}
            >
              Configure compliance →
            </button>
          )}
        </div>
        <DisclaimerNote />

        {showSetup && (
          <ComplianceSetupModal
            account={account}
            onClose={() => setShowSetup(false)}
            onSaved={(updatedFields) => { setAccount(a => ({...a, ...updatedFields})); setShowSetup(false); if (onSaved) onSaved(); }}
          />
        )}
      </Card>
    );
  }

  const { ddResult, todayPnL, totalPnL, consistency, dllUsed, dllLimit, dllPct, targetPct } = compliance;

  // ── Configured ──────────────────────────────────────────────
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <SectionTitle>📊 Prop Firm Compliance</SectionTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <DrawdownTypeBadge type={drawdown_type} />
          {true && (
            <button
              onClick={handleSetup}
              style={{ background: "none", border: "none", color: C.muted, fontSize: 11, cursor: "pointer" }}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

        {/* 1. Drawdown Buffer — the most critical gauge */}
        {ddResult && (
          <ComplianceGauge
            label="Drawdown Buffer"
            sublabel={`${drawdown_type === "static" ? "Static" : "EOD Trailing"} · Floor ${fmt$(ddResult.floor)}`}
            value={ddResult.bufferPct}
            displayValue={fmt$(ddResult.buffer)}
            lowIsGood={false}
            thresholds={{ warn: 25, danger: 10 }}
            invertDanger // low pct = danger
          />
        )}

        {/* 2. Daily Loss Limit */}
        {dllLimit != null && dllLimit > 0 && (
          <ComplianceGauge
            label="Daily Loss Limit"
            sublabel={`${fmt$(dllUsed)} used of ${fmt$(dllLimit)} today`}
            value={dllPct}
            displayValue={`${Math.round(dllPct || 0)}%`}
            thresholds={{ warn: 50, danger: 80 }}
          />
        )}

        {/* 3. Profit Target */}
        {profit_target > 0 && targetPct !== null && (
          <ProgressBar
            label="Profit Target"
            sublabel={`${fmt$(Math.max(0, totalPnL))} of ${fmt$(profit_target)}`}
            pct={targetPct}
            color={targetPct >= 100 ? C.green : C.blue}
            badge={targetPct >= 100 ? "✓ TARGET HIT" : null}
            badgeColor={C.green}
          />
        )}

        {/* 4. Consistency Rule */}
        {consistency && consistency_rule > 0 && (
          <ConsistencyRow consistency={consistency} />
        )}

      </div>

      {/* Summary line */}
      <AccountSummaryRow account={account} ddResult={ddResult} />

      <DisclaimerNote />

      {showSetup && (
        <ComplianceSetupModal
          account={account}
          onClose={() => setShowSetup(false)}
          onSaved={(updatedFields) => { setAccount(a => ({...a, ...updatedFields})); setShowSetup(false); if (onSaved) onSaved(); }}
        />
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

// Generic compliance gauge with color-coded fill
function ComplianceGauge({ label, sublabel, value, displayValue, thresholds, invertDanger = false }) {
  const pct = Math.min(100, Math.max(0, value || 0));
  const isDanger  = invertDanger ? pct <= thresholds.danger  : pct >= thresholds.danger;
  const isWarning = invertDanger ? pct <= thresholds.warn    : pct >= thresholds.warn;
  const barColor  = isDanger ? C.red : isWarning ? C.yellow : C.green;

  return (
    <div style={{
      padding: "10px 12px",
      background: isDanger ? C.red + "0a" : isWarning ? C.yellow + "08" : "rgba(255,255,255,0.02)",
      border: `1px solid ${isDanger ? C.red + "33" : isWarning ? C.yellow + "33" : C.border}`,
      borderRadius: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <div>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{label}</span>
          {sublabel && (
            <span style={{ fontSize: 10, color: C.muted, marginLeft: 8 }}>{sublabel}</span>
          )}
        </div>
        <span style={{
          fontSize: 13, fontWeight: 800, color: barColor, fontFamily: "monospace",
        }}>
          {displayValue}
        </span>
      </div>
      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${invertDanger ? pct : pct}%`,
          background: barColor,
          borderRadius: 3,
          transition: "width 0.4s ease",
        }} />
      </div>
      {isDanger && (
        <div style={{ fontSize: 10, color: C.red, marginTop: 5, fontWeight: 600 }}>
          ⚠ Near violation — reduce position size or stop trading today
        </div>
      )}
    </div>
  );
}

// Simple progress bar (for profit target)
function ProgressBar({ label, sublabel, pct, color, badge, badgeColor }) {
  const p = Math.min(100, Math.max(0, pct || 0));
  return (
    <div style={{
      padding: "10px 12px",
      background: "rgba(255,255,255,0.02)",
      border: `1px solid ${C.border}`,
      borderRadius: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <div>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{label}</span>
          {sublabel && (
            <span style={{ fontSize: 10, color: C.muted, marginLeft: 8 }}>{sublabel}</span>
          )}
        </div>
        {badge ? (
          <span style={{
            fontSize: 10, fontWeight: 700, color: badgeColor,
            background: badgeColor + "20", padding: "2px 8px",
            border: `1px solid ${badgeColor}40`, borderRadius: 4,
          }}>
            {badge}
          </span>
        ) : (
          <span style={{ fontSize: 13, fontWeight: 800, color, fontFamily: "monospace" }}>
            {Math.round(p)}%
          </span>
        )}
      </div>
      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${p}%`, background: color,
          borderRadius: 3, transition: "width 0.4s ease",
        }} />
      </div>
    </div>
  );
}

// Consistency rule row with the critical math explained inline
function ConsistencyRow({ consistency }) {
  const { bestDay, totalProfit, consistencyPct, limit, isViolation } = consistency;
  const pct = Math.min(100, (consistencyPct / limit) * 100);
  const color = isViolation ? C.red : pct > 75 ? C.yellow : C.green;

  return (
    <div style={{
      padding: "10px 12px",
      background: isViolation ? C.red + "0a" : "rgba(255,255,255,0.02)",
      border: `1px solid ${isViolation ? C.red + "33" : C.border}`,
      borderRadius: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Consistency Rule</span>
        <span style={{ fontSize: 13, fontWeight: 800, color, fontFamily: "monospace" }}>
          {(consistencyPct * 100).toFixed(0)}% <span style={{ fontSize: 10, color: C.muted }}>/ {(limit * 100).toFixed(0)}% max</span>
        </span>
      </div>
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 6, lineHeight: 1.5 }}>
        Best day {fmt$(bestDay)} ÷ total profit {fmt$(totalProfit)}
      </div>
      <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 2, transition: "width 0.4s ease" }} />
      </div>
      {isViolation && (
        <div style={{ fontSize: 10, color: C.red, marginTop: 5, fontWeight: 600 }}>
          ⚠ Best day exceeds the {(limit * 100).toFixed(0)}% consistency cap — payout may be blocked
        </div>
      )}
    </div>
  );
}

function DrawdownTypeBadge({ type }) {
  const label = type === "static" ? "Static DD" : type === "intraday_trailing" ? "Intraday Trail" : "EOD Trail";
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, color: C.muted,
      background: C.border, padding: "2px 7px",
      borderRadius: 3, letterSpacing: "0.06em", textTransform: "uppercase",
    }}>
      {label}
    </span>
  );
}

function AccountSummaryRow({ account, ddResult }) {
  if (!ddResult) return null;
  const { prop_firm, account_type } = account;
  const criticalPct = ddResult.bufferPct;
  const statusColor = criticalPct <= 10 ? C.red : criticalPct <= 25 ? C.yellow : C.green;
  const statusLabel = criticalPct <= 10 ? "Critical" : criticalPct <= 25 ? "Caution" : "Compliant";

  return (
    <div style={{
      marginTop: 12,
      padding: "8px 12px",
      background: statusColor + "08",
      border: `1px solid ${statusColor}25`,
      borderRadius: 6,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}>
      <span style={{ fontSize: 11, color: C.muted }}>
        {prop_firm || "Custom"} · {account_type?.toUpperCase() || "FUNDED"} · Balance {fmt$(ddResult.currentBalance)}
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, color: statusColor }}>
        ● {statusLabel}
      </span>
    </div>
  );
}

function DisclaimerNote() {
  return (
    <div style={{
      marginTop: 10, fontSize: 10, color: C.muted, lineHeight: 1.5,
      padding: "6px 10px", background: "#1a1d2e",
      border: `1px solid ${C.border}`, borderRadius: 6,
    }}>
      ⚠ Based on synced trade data, not a live broker feed. Verify against your firm's dashboard before making risk decisions. Drawdown rules vary by firm — always check your specific account terms.
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ComplianceSetupModal
// Inline modal — saves compliance fields directly to the account
// row in Supabase. No page navigation required.
// ─────────────────────────────────────────────────────────────
function ComplianceSetupModal({ account, onClose, onSaved }) {
  const [ddLimit,         setDdLimit]         = useState(account.drawdown_limit != null ? String(account.drawdown_limit) : "");
  const [ddType,          setDdType]          = useState(account.drawdown_type || "eod_trailing");
  const [dailyLossLimit,  setDailyLossLimit]  = useState(account.daily_loss_limit != null ? String(account.daily_loss_limit) : "");
  const [profitTarget,    setProfitTarget]    = useState(account.profit_target != null ? String(account.profit_target) : "");
  const [consistencyRule, setConsistencyRule] = useState(account.consistency_rule != null ? String(Math.round(account.consistency_rule * 100)) : "");
  const [saving, setSaving] = useState(false);

  // Auto-apply known firm presets
  const applyPreset = useCallback(() => {
    const preset = getFirmPreset(account.prop_firm, account.starting_balance);
    if (!preset) return;
    if (preset.drawdown_limit)  setDdLimit(String(preset.drawdown_limit));
    if (preset.drawdown_type)   setDdType(preset.drawdown_type);
    if (preset.profit_target)   setProfitTarget(String(preset.profit_target));
    if (preset.consistency_rule != null) setConsistencyRule(String(Math.round(preset.consistency_rule * 100)));
  }, [account]);

  const save = async () => {
    if (!ddLimit) { alert("Max Drawdown Limit is required to enable compliance tracking."); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/accounts?id=eq.${account.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({
          drawdown_limit:   parseFloat(ddLimit) || null,
          drawdown_type:    ddType || "eod_trailing",
          daily_loss_limit: parseFloat(dailyLossLimit) || null,
          profit_target:    parseFloat(profitTarget) || null,
          consistency_rule: consistencyRule !== "" ? parseFloat(consistencyRule) / 100 : null,
        }),
      });
      if (res.ok) {
        onSaved({
          drawdown_limit:   parseFloat(ddLimit) || null,
          drawdown_type:    ddType || "eod_trailing",
          daily_loss_limit: parseFloat(dailyLossLimit) || null,
          profit_target:    parseFloat(profitTarget) || null,
          consistency_rule: consistencyRule !== "" ? parseFloat(consistencyRule) / 100 : null,
        });
      }
      else { const e = await res.json(); alert("Save failed: " + (e.message || e.error)); }
    } catch (e) {
      alert("Network error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const inp = (extra = {}) => ({
    width: "100%", boxSizing: "border-box",
    padding: "9px 12px", background: "#0a0d1a",
    border: `1px solid ${C.border}`, borderRadius: 8,
    color: C.text, fontSize: 13, marginBottom: 12,
    outline: "none", ...extra,
  });

  const hasFirmPreset = !!FIRM_COMPLIANCE_PRESETS[account.prop_firm];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 2000, padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#0f1322", border: `1px solid ${C.border}`,
          borderRadius: 12, padding: 24, maxWidth: 440, width: "100%",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
            Compliance Setup
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 16 }}>
          {account.prop_firm || "Custom"} · {account.name}
        </div>

        {hasFirmPreset && (
          <button
            onClick={applyPreset}
            style={{
              width: "100%", padding: "8px", marginBottom: 16,
              background: C.blue + "15", border: `1px solid ${C.blue}40`,
              borderRadius: 8, color: C.blue, fontSize: 12,
              fontWeight: 700, cursor: "pointer",
            }}
          >
            ⚡ Auto-fill {account.prop_firm} defaults for {fmt$(account.starting_balance)} account
          </button>
        )}

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>
          Max Drawdown Limit ($) <span style={{ color: C.red }}>*</span>
        </label>
        <input type="number" value={ddLimit} onChange={e => setDdLimit(e.target.value)} placeholder="e.g. 2500" style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Drawdown Type</label>
        <select value={ddType} onChange={e => setDdType(e.target.value)} style={inp()}>
          <option value="eod_trailing">EOD Trailing — Topstep, Apex, MFF (most common)</option>
          <option value="static">Static — floor never moves</option>
          <option value="intraday_trailing">Intraday Trailing — Apex Intraday</option>
        </select>

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Daily Loss Limit ($) <span style={{ color: C.muted }}>(optional)</span></label>
        <input type="number" value={dailyLossLimit} onChange={e => setDailyLossLimit(e.target.value)} placeholder="e.g. 1000" style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Profit Target ($) <span style={{ color: C.muted }}>(optional — eval accounts)</span></label>
        <input type="number" value={profitTarget} onChange={e => setProfitTarget(e.target.value)} placeholder="e.g. 3000" style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Consistency Rule (%) <span style={{ color: C.muted }}>(0 = none · 50 = Topstep/Apex)</span></label>
        <input type="number" min={0} max={100} value={consistencyRule} onChange={e => setConsistencyRule(e.target.value)} placeholder="e.g. 50" style={inp()} />

        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "10px", background: "transparent",
              border: `1px solid ${C.border}`, borderRadius: 8,
              color: C.muted, fontSize: 13, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            style={{
              flex: 2, padding: "10px", background: C.blue,
              border: "none", borderRadius: 8,
              color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving…" : "Save Compliance Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
